import {
  DeleteWebhookEndpoint,
  GetWebhookDelivery,
  GetWebhookEndpoints,
  PostCancelWebhookDelivery,
  PostResendWebhookDelivery,
  PostRotateWebhookEndpointSecret,
  PostWebhookDeliveries,
  PostWebhookEndpoint,
  PostWebhookEndpointTest,
  PutWebhookEndpoint,
} from "@/server-types";
import type { AxiosError } from "axios";
import type {
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import { del, get, post, put } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { queryClient } from "..";

type QueryOpts<TData> = Omit<
  UseQueryOptions<TData, AxiosError, TData>,
  "queryKey" | "queryFn"
> & { displayNotification?: boolean };

type MutationOpts<TData, TVariables> = Omit<
  UseMutationOptions<TData, AxiosError, TVariables>,
  "mutationFn" | "onError"
> & {
  displayNotification?: boolean;
  onError?: (
    error: AxiosError,
    variables: TVariables,
    context: unknown,
    processedError: string | null,
    processedErrorCode: string | null,
  ) => void;
};

const ENDPOINTS_KEY = ["webhook-endpoints"];
const DELIVERIES_KEY = ["webhook-deliveries"];

function invalidateEndpoints() {
  queryClient.invalidateQueries({ queryKey: ENDPOINTS_KEY });
}

function invalidateDeliveries() {
  queryClient.invalidateQueries({ queryKey: DELIVERIES_KEY });
}

export const useGetEndpoints = (
  options?: QueryOpts<GetWebhookEndpoints["response"]>,
) => {
  return useCustomQuery({
    queryKey: ENDPOINTS_KEY,
    queryFn: () => get<GetWebhookEndpoints>("/webhook-endpoint"),
    ...options,
  });
};

export const usePostEndpoint = (
  options?: MutationOpts<
    PostWebhookEndpoint["response"],
    PostWebhookEndpoint["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: (body: PostWebhookEndpoint["body"]) =>
      post<PostWebhookEndpoint>("/webhook-endpoint", body),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateEndpoints();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const usePutEndpoint = (
  options?: MutationOpts<
    PutWebhookEndpoint["response"],
    { webhookEndpointId: number; body: PutWebhookEndpoint["body"] }
  >,
) => {
  return useCustomMutation({
    mutationFn: ({
      webhookEndpointId,
      body,
    }: {
      webhookEndpointId: number;
      body: PutWebhookEndpoint["body"];
    }) =>
      put<PutWebhookEndpoint>(`/webhook-endpoint/${webhookEndpointId}`, body),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateEndpoints();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useDeleteEndpoint = (
  options?: MutationOpts<
    DeleteWebhookEndpoint["response"],
    { webhookEndpointId: number }
  >,
) => {
  return useCustomMutation({
    mutationFn: ({ webhookEndpointId }: { webhookEndpointId: number }) =>
      del<DeleteWebhookEndpoint>(`/webhook-endpoint/${webhookEndpointId}`),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateEndpoints();
      invalidateDeliveries();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useRotateSecret = (
  options?: MutationOpts<
    PostRotateWebhookEndpointSecret["response"],
    { webhookEndpointId: number }
  >,
) => {
  return useCustomMutation({
    mutationFn: ({ webhookEndpointId }: { webhookEndpointId: number }) =>
      post<PostRotateWebhookEndpointSecret>(
        `/webhook-endpoint/${webhookEndpointId}/rotate-secret`,
        {},
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateEndpoints();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useSendTestEvent = (
  options?: MutationOpts<
    PostWebhookEndpointTest["response"],
    { webhookEndpointId: number }
  >,
) => {
  return useCustomMutation({
    mutationFn: ({ webhookEndpointId }: { webhookEndpointId: number }) =>
      post<PostWebhookEndpointTest>(
        `/webhook-endpoint/${webhookEndpointId}/test`,
        {},
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateDeliveries();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useGetDeliveries = (
  body: PostWebhookDeliveries["body"],
  options?: QueryOpts<PostWebhookDeliveries["response"]>,
) => {
  return useCustomQuery({
    queryKey: [...DELIVERIES_KEY, body],
    queryFn: () =>
      post<PostWebhookDeliveries>("/webhook-endpoint/deliveries", body),
    ...options,
  });
};

export const useGetDelivery = (
  { webhookDeliveryId }: { webhookDeliveryId: number },
  options?: QueryOpts<GetWebhookDelivery["response"]>,
) => {
  const { enabled, ...rest } = options ?? {};
  return useCustomQuery({
    queryKey: ["webhook-delivery", webhookDeliveryId],
    queryFn: () =>
      get<GetWebhookDelivery>(
        `/webhook-endpoint/delivery/${webhookDeliveryId}`,
      ),
    ...rest,
    enabled: (enabled ?? true) && webhookDeliveryId > 0,
  });
};

export const useCancelDelivery = (
  options?: MutationOpts<
    PostCancelWebhookDelivery["response"],
    { webhookDeliveryId: number }
  >,
) => {
  return useCustomMutation({
    mutationFn: ({ webhookDeliveryId }: { webhookDeliveryId: number }) =>
      post<PostCancelWebhookDelivery>(
        `/webhook-endpoint/delivery/${webhookDeliveryId}/cancel`,
        {},
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateDeliveries();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useResendDelivery = (
  options?: MutationOpts<
    PostResendWebhookDelivery["response"],
    { webhookDeliveryId: number }
  >,
) => {
  return useCustomMutation({
    mutationFn: ({ webhookDeliveryId }: { webhookDeliveryId: number }) =>
      post<PostResendWebhookDelivery>(
        `/webhook-endpoint/delivery/${webhookDeliveryId}/resend`,
        {},
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateDeliveries();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
